import { useState } from "react";
import { usePage, router } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, Gamepad2, Plus, Trash2 } from "lucide-react";

export default function PlaysIndex() {
  const { event, play_records, my_games, paths } = usePage().props;
  const [showForm, setShowForm] = useState(false);

  return (
    <AppLayout>
      <div className="container py-8 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Plays</h1>
            <p className="text-sm text-muted-foreground">{event.title}</p>
          </div>
          <Button size="sm" onClick={() => setShowForm((v) => !v)}>
            <Plus className="h-4 w-4" />
            Log Play
          </Button>
        </div>

        {showForm && (
          <LogPlayForm
            event={event}
            games={my_games}
            paths={paths}
            onClose={() => setShowForm(false)}
          />
        )}

        <PlaysList records={play_records} paths={paths} />
      </div>
    </AppLayout>
  );
}

function LogPlayForm({ event, games = [], paths, onClose }) {
  const defaultDate = event.starts_at
    ? new Date(event.starts_at).toISOString().split("T")[0]
    : new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    bgg_game_id:      "",
    num_players:      "",
    duration_minutes: "",
    played_on:        defaultDate,
  });

  function submit(e) {
    e.preventDefault();
    router.post(paths.plays, form, { onSuccess: onClose });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-5 mb-6">
      <h2 className="font-semibold mb-4">Log a Play</h2>
      <form onSubmit={submit} className="space-y-3">
        <div className="space-y-1.5">
          <Label>Game</Label>
          <select
            className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
            value={form.bgg_game_id}
            onChange={(e) => setForm({ ...form, bgg_game_id: e.target.value })}
            required
          >
            <option value="">Select a game…</option>
            {games.map((g) => (
              <option key={g.bgg_id} value={g.bgg_id}>{g.title}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Players</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={form.num_players}
              onChange={(e) => setForm({ ...form, num_players: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Duration (min)</Label>
            <Input
              type="number"
              min="1"
              placeholder="Optional"
              value={form.duration_minutes}
              onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Date Played</Label>
          <Input
            type="date"
            value={form.played_on}
            onChange={(e) => setForm({ ...form, played_on: e.target.value })}
            required
          />
        </div>

        <div className="flex gap-2">
          <Button type="submit" size="sm">Save Play</Button>
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}

function PlaysList({ records, paths }) {
  if (records.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Gamepad2 className="h-8 w-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No plays logged yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {records.map((record) => (
        <PlayRow key={record.id} record={record} paths={paths} />
      ))}
    </div>
  );
}

function PlayRow({ record, paths }) {
  const { auth } = usePage().props;
  const canDelete = auth.user?.id === record.user.id;

  function deleteRecord() {
    if (!confirm("Remove this play record?")) return;
    router.delete(`${paths.plays}/${record.id}`);
  }

  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-sm">{record.bgg_game.title}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
            <span>{record.num_players} players</span>
            {record.duration_minutes && <span>· {record.duration_minutes} min</span>}
            <span>· by {record.user.full_name}</span>
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {new Date(record.played_on).toLocaleDateString()}
            </span>
            {record.bgg_play_id && (
              <span className="text-green-600 dark:text-green-400">· BGG logged</span>
            )}
            {record.bgg_error && (
              <span className="text-amber-600 dark:text-amber-400">· BGG sync failed</span>
            )}
          </div>
        </div>
        {canDelete && (
          <button
            onClick={deleteRecord}
            className="text-muted-foreground hover:text-destructive transition-colors shrink-0 mt-0.5"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
